import React, { useEffect, useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useRemScale } from 'lib/remScale';
import { TYPE, FONTS, TITLE_SIZE } from 'style/typography';
import * as api from 'utils/relicVault/collectionApi';
import { Trophy } from 'components/Icons';
import FocusRing from './FocusRing';

/** The first visit's three badges: the slide, the tutor and the quiz (Sol badge-contract; 50 QuikCoins each). */
export const TOUR_BADGES = [
  { id: 'WEL-001', name: 'Slide Explorer' },
  { id: 'AIT-001', name: 'First Friend' },
  { id: 'WEL-003', name: 'Quiz Starter' },
];
export const TOUR_COINS = 150;

/** The three badges' pictures by id, fetched once when the tour starts so the panel opens with them (Sol E4 A). */
let artRequest = null;
export function prefetchTourBadges({ token } = {}) {
  if (!artRequest) {
    artRequest = api
      .fetchBadgeCatalog({ token })
      .then(categories => {
        const found = {};
        (categories || []).forEach(category =>
          (category?.badges || []).forEach(badge => {
            const id = badge?.badgeId || badge?.id;
            if (id) found[id] = badge?.imageUrl || badge?.image || null;
          }),
        );
        return found;
      })
      .catch(() => {
        artRequest = null; // the panel asks again
        return {};
      });
  }
  return artRequest;
}

/**
 * "You did it!" (prototype stage "explore"; owner rounds 10-12): the three badges and their coins, then Explore more
 * worlds, which goes to the portal's "Which subject first?" and from there into Quikbase for the controls.
 */
export default function TourDonePanel({ token, onExplore }) {
  const { rem } = useRemScale();
  const [art, setArt] = useState({});

  useEffect(() => {
    let cancelled = false;
    prefetchTourBadges({ token }).then(found => {
      if (!cancelled) setArt(found);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    // The owner's prototype "You did it!" (rounds 10-12): over the dimmed world - FIRST VISIT DONE, the title, the three
    // badges earned, their QuikCoins, and Explore more worlds outlined with the hand (round 13.7); since round 23 in one
    // framed window (Sol round23-audit-tour 15/16: 600px, 24 padding, 16 gaps, 120px badges, one coin, a 48px button).
    <View style={styles.overlay} testID="first-visit-done">
      <View style={styles.frame}>
      <Text style={[styles.eyebrow, { fontSize: TYPE.sm, fontFamily: FONTS.body }]}>FIRST VISIT DONE</Text>
      <Text style={[styles.title, { fontSize: TITLE_SIZE, fontFamily: FONTS.body, marginTop: 8 }]}>You did it!</Text>
      <View style={[styles.badges, { marginTop: 16 }]}>
        {TOUR_BADGES.map(badge => (
          <View key={badge.id} style={[styles.badge, { width: 120, marginHorizontal: 8 }]}>
            {art[badge.id] ? (
              <Image source={{ uri: art[badge.id] }} style={{ width: 120, height: 120 }} resizeMode="contain" />
            ) : (
              // Never a blank circle: a trophy stands in until (or unless) the picture arrives.
              <View style={[styles.badgeBlank, { width: 120, height: 120, borderRadius: 60 }]}>
                <Trophy size={48} color="#FCD34D" />
              </View>
            )}
            <Text style={[styles.badgeName, { fontSize: TYPE.sm, fontFamily: FONTS.body, marginTop: 8, textAlign: 'center' }]}>{badge.name}</Text>
            <Text style={[styles.earned, { fontSize: TYPE.sm, fontFamily: FONTS.body }]}>Earned!</Text>
          </View>
        ))}
      </View>
      <View style={[styles.coinsPill, { marginTop: 16, paddingHorizontal: 16, height: 40, borderRadius: 20 }]}>
        <View style={styles.coin}><Text style={[styles.coinQ, { fontSize: TYPE.sm, fontFamily: FONTS.body }]}>Q</Text></View>
        <Text style={[styles.coins, { fontSize: TYPE.xl, fontFamily: FONTS.body, marginLeft: 8 }]}>{`+${TOUR_COINS} QuikCoins`}</Text>
      </View>
      <TouchableOpacity
        testID="first-visit-explore"
        accessibilityRole="button"
        activeOpacity={0.85}
        onPress={onExplore}
        style={[styles.cta, { borderRadius: 12, height: 48, paddingHorizontal: 24, marginTop: 16 }]}>
        <Text style={[styles.ctaText, { fontSize: TYPE.base, fontFamily: FONTS.body }]}>Explore more worlds</Text>
      </TouchableOpacity>
      <FocusRing targetId="first-visit-explore" />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 92,
  },
  eyebrow: { color: '#C5E836', fontWeight: '600', letterSpacing: 2 },
  title: { color: '#F6FBFF', fontWeight: '700' },
  frame: { width: 600, maxWidth: '92%', alignItems: 'center', padding: 24, borderRadius: 16, backgroundColor: 'rgba(12,14,18,0.9)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  earned: { color: 'rgba(246,251,255,0.6)', marginTop: 2, fontWeight: '400' },
  coinsPill: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(252,211,77,0.12)', borderWidth: 1, borderColor: 'rgba(252,211,77,0.45)' },
  coin: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FCD34D', borderWidth: 2, borderColor: '#B8860B' },
  coinQ: { color: '#7A5300', fontWeight: '700' },
  badges: { flexDirection: 'row', justifyContent: 'center' },
  badge: { alignItems: 'center' },
  badgeBlank: { alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(197,232,54,0.18)', borderWidth: 2, borderColor: 'rgba(252,211,77,0.55)' },
  badgeName: { color: '#F6FBFF', fontWeight: '600' },
  coins: { color: '#FCD34D', fontWeight: '600' },
  cta: { backgroundColor: '#C5E836', alignItems: 'center', justifyContent: 'center' },
  ctaText: { color: '#10120A', fontWeight: '700' },
});
